import { api_fetch, api_fetch_throws } from "@/api/client.tsx"
import { MonthClose } from "@/api/month_close.tsx"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { setMonthCloses } from "@/org_config/state.tsx"
import { format, parseISO } from "date-fns"
import { Loader2, Trash2 } from "lucide-react"
import { useState } from "preact/hooks"
import useSWR from "swr"

const MONTH_CLOSES_KEY = "month-closes"

/** "2026-07" (what `<input type="month">` yields) → the first of that month, or null if malformed. */
export function month_input_to_date(value: string): string | null {
  return /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : null
}

/**
 * Month-end close dates. Each row says when the books for a month were finished, which is the
 * last validation date that belongs on that month's month-end close list (see `shared/clearing_list.ts`).
 */
export function MonthClosesSettings() {
  const { data: closes, mutate } = useSWR(MONTH_CLOSES_KEY, () => api_fetch_throws<MonthClose[]>("/month-closes"))
  const [month, set_month] = useState("")
  const [closed_on, set_closed_on] = useState("")
  const [saving, set_saving] = useState(false)

  async function refresh() {
    const latest = await mutate()
    if (latest) setMonthCloses(latest)
  }

  async function save(event: Event) {
    event.preventDefault()
    const month_date = month_input_to_date(month)
    if (!month_date || !closed_on) {
      toast({ variant: "destructive", title: "Pick a month and the day it closed" })
      return
    }
    set_saving(true)
    const result = await api_fetch<MonthClose>("/month-closes", {
      method: "PUT",
      body: { month: month_date, closed_on },
    })
    set_saving(false)
    if (!result.ok) {
      toast({ variant: "destructive", title: "Could not save close date", description: result.error.message })
      return
    }
    set_month("")
    set_closed_on("")
    await refresh()
  }

  async function remove(close: MonthClose) {
    const result = await api_fetch(`/month-closes/${close.id}`, { method: "DELETE" })
    if (!result.ok) {
      toast({ variant: "destructive", title: "Could not remove close date", description: result.error.message })
      return
    }
    await refresh()
  }

  return (
    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        The day each month's books were finished. Anything validated on or before that day is on that
        month's close list; anything after it rolls into the next month. The Month-End Close List
        column and export use these dates.
      </p>
      {closes === undefined ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      ) : closes.length === 0 ? (
        <p class="text-sm text-gray-500">No close dates recorded yet.</p>
      ) : (
        <table class="text-sm">
          <thead>
            <tr class="text-left text-gray-500">
              <th class="pr-8 font-medium">Month</th>
              <th class="pr-8 font-medium">Closed on</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {closes.map((close: MonthClose) => (
              <tr key={close.id}>
                <td class="pr-8 py-1">{format(parseISO(close.month), "MMMM yyyy")}</td>
                <td class="pr-8 py-1">{format(parseISO(close.closed_on), "MMM d, yyyy")}</td>
                <td class="py-1">
                  <Button variant="ghost" size="sm" onClick={() => void remove(close)} title="Remove">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form class="flex flex-wrap items-end gap-3" onSubmit={save}>
        <div>
          <Label htmlFor="month-close-month">Month</Label>
          <Input
            id="month-close-month"
            type="month"
            value={month}
            onInput={(event: Event) => set_month((event.target as HTMLInputElement).value)}
          />
        </div>
        <div>
          <Label htmlFor="month-close-closed-on">Closed on</Label>
          <Input
            id="month-close-closed-on"
            type="date"
            value={closed_on}
            onInput={(event: Event) => set_closed_on((event.target as HTMLInputElement).value)}
          />
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save close date"}
        </Button>
      </form>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("month_input_to_date", () => {
    it("turns a month picker value into the first of the month", () => {
      expect(month_input_to_date("2026-07")).toBe("2026-07-01")
    })

    it("rejects anything that is not YYYY-MM", () => {
      expect(month_input_to_date("")).toBeNull()
      expect(month_input_to_date("2026-7")).toBeNull()
      expect(month_input_to_date("2026-07-09")).toBeNull()
    })
  })
}
